'use client';

import React, { useState } from 'react';
import useClassNames from 'helpers/hooks/useClassNames';
import usePromoCode from 'frontastic/hooks/usePromoCode';

export interface CouponLinkProps {
  code: string;
  className?: string;
}

/**
 * Applies a discount code when clicked. Rendered in place of an ordinary hyperlink wherever
 * a Contentful author links to `/apply-coupon/<CODE>`, so marketing can drop a working
 * coupon into any body copy without a code change per placement.
 *
 * A button, not an anchor: there is no page to navigate to, and an anchor would let the
 * shopper open a dead route in a new tab.
 */
const CouponLink: React.FC<React.PropsWithChildren<CouponLinkProps>> = ({ code, className, children }) => {
  const { applyPromoCode } = usePromoCode();
  const [processing, setProcessing] = useState(false);

  const buttonClassName = useClassNames([
    'cursor-pointer font-bold leading-loose tracking-normal text-cta-fill-primary underline disabled:cursor-wait disabled:opacity-60',
    className,
  ]);

  const handleClick = async () => {
    setProcessing(true);
    try {
      await applyPromoCode(code);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <button type="button" className={buttonClassName} disabled={processing} onClick={handleClick}>
      {children}
    </button>
  );
};

export default CouponLink;
